/* ═══════════════════════════════════════════════════════════════════════════
   cnc-search.js — type-ahead for any CNC search field, plus the two small
   behaviours the results page needs (clear button, did-you-mean).

   WHAT IT DOES NOT DO: template results. The panel's contents are rendered by
   sections/cnc-predictive.liquid through Shopify's section rendering API, so
   the discount maths, the ₹ formatting and the image sizing are the same
   Liquid the rest of the site uses. This file fetches HTML and puts it in a
   box; everything a customer reads was rendered server-side.

   ── Runs many times ────────────────────────────────────────────────────────
   Like every CNC asset, this file can be included by more than one section on
   a page, and the browser will execute it once per <script> tag. Anything that
   must exist once — the delegated key handler, the recent-search store — hangs
   off `window`, and every mount is idempotent via a dataset flag on the input.

   ── Requests ───────────────────────────────────────────────────────────────
   No `Accept: application/json` header, ever. The section rendering endpoints
   change what they return when they see one, which is the bug that already cost
   this theme a day on the cart (see the note in cnc-app.js's fetchSections).
   We ask for HTML by asking for a section, and read it as text.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  if (window.__cncSearchBooted) return;
  window.__cncSearchBooted = true;

  var SECTION = "cnc-predictive";
  var DEBOUNCE = 180;       /* ms after the last keystroke */
  var MIN_CHARS = 2;
  var RECENT_KEY = "cnc:recent-searches";
  var RECENT_MAX = 6;

  /* Query → HTML. Bounded because a customer typing "chew toy for puppies"
     produces one entry per prefix and an unbounded map on a long session is a
     slow leak nobody looks for. */
  var cache = new Map();
  var CACHE_MAX = 40;

  function cacheGet(k) { return cache.get(k); }
  function cacheSet(k, v) {
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(k, v);
  }

  /* ── Recent searches ─────────────────────────────────────────────────────
     localStorage can throw outright — Safari in private mode, a browser set to
     block site data, an iframe with storage partitioned off. Every read and
     write is wrapped, and a failure degrades to "no recents" rather than
     taking search down with it. */
  function recentRead() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(function (s) { return typeof s === "string"; }) : [];
    } catch (e) { return []; }
  }
  function recentAdd(term) {
    term = (term || "").trim();
    if (term.length < MIN_CHARS) return;
    try {
      var list = recentRead().filter(function (s) {
        return s.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* storage unavailable — recents are a convenience, not state */ }
  }
  function recentClear() {
    try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
  }

  /* ── Small helpers ───────────────────────────────────────────────────────── */

  /* cnc-icon is a Liquid snippet and this markup is built in the browser, so
     the one icon the client renders is inlined. Stroke, 1.7, currentColor —
     the same drawing rules as the rest of the set, so it sits level with the
     server-rendered rows above it. */
  var CLOCK =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>';

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function searchUrl(term) {
    return (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : "/") +
      "search?q=" + encodeURIComponent(term) + "&type=product";
  }

  function suggestUrl(term, limit, sectionId) {
    var u = "/search/suggest?q=" + encodeURIComponent(term) +
      "&resources[type]=product,collection,query" +
      "&resources[limit]=" + (limit || 6) +
      "&resources[options][unavailable_products]=last";
    if (sectionId) u += "&section_id=" + sectionId;
    return u;
  }

  /* Wraps every case-insensitive occurrence of `term` in <b>, on text nodes
     only. Done in the DOM rather than with a string replace on innerHTML
     because the server already escaped this text and a regex over markup would
     happily match inside an attribute. */
  function highlight(scope, term) {
    if (!term) return;
    var needle = term.trim().toLowerCase();
    if (!needle) return;
    var marks = scope.querySelectorAll("[data-cnc-ps-mark]");
    for (var i = 0; i < marks.length; i++) {
      var el = marks[i];
      var text = el.textContent;
      var low = text.toLowerCase();
      var at = low.indexOf(needle);
      if (at === -1) continue;
      var frag = document.createDocumentFragment();
      frag.appendChild(document.createTextNode(text.slice(0, at)));
      var b = document.createElement("b");
      b.textContent = text.slice(at, at + needle.length);
      frag.appendChild(b);
      frag.appendChild(document.createTextNode(text.slice(at + needle.length)));
      el.textContent = "";
      el.appendChild(frag);
    }
  }

  /* ── The panel ───────────────────────────────────────────────────────────── */

  function Panel(input) {
    this.input = input;
    this.limit = parseInt(input.getAttribute("data-cnc-ps-limit"), 10) || 6;
    this.host = input.closest(".cnc-find-field, [data-cnc-hd-field], label") || input.parentElement;
    this.host.classList.add("cnc-ps-host");

    this.el = document.createElement("div");
    this.el.className = "cnc-ps";
    this.el.id = "cnc-ps-" + Math.random().toString(36).slice(2, 8);
    this.el.setAttribute("role", "listbox");
    this.el.setAttribute("aria-label", "Search suggestions");
    this.el.hidden = true;
    this.host.appendChild(this.el);

    this.open = false;
    this.options = [];
    this.active = -1;
    this.timer = null;
    this.abort = null;
    this.lastQuery = "";

    /* Trending terms are optional and page-supplied: the search page prints
       them, other pages don't, and the panel simply shows one fewer group
       where they're absent. */
    this.trending = [];
    var blob = document.querySelector("[data-cnc-ps-trending]");
    if (blob) {
      try { this.trending = JSON.parse(blob.textContent) || []; } catch (e) { this.trending = []; }
    }

    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", this.el.id);
    input.setAttribute("autocomplete", "off");

    this.bind();
  }

  Panel.prototype.bind = function () {
    var self = this;
    var input = this.input;

    input.addEventListener("input", function () { self.onType(); });
    input.addEventListener("focus", function () { self.onType(); });

    input.addEventListener("keydown", function (e) { self.onKey(e); });

    /* Closing on blur alone loses the click: the mousedown that lands on a row
       blurs the input, the panel hides, and the click hits nothing. Deferring
       past the click is the usual fix and is fragile on touch; watching where
       focus actually went is not. */
    input.addEventListener("blur", function () {
      setTimeout(function () {
        if (!self.el.contains(document.activeElement)) self.close();
      }, 0);
    });

    document.addEventListener("pointerdown", function (e) {
      if (!self.el.contains(e.target) && e.target !== input) self.close();
    });

    /* Pointer and keyboard must never disagree about the active row. */
    this.el.addEventListener("pointerenter", function (e) {
      var opt = e.target.closest ? e.target.closest("[data-cnc-ps-opt]") : null;
      if (!opt) return;
      var i = self.options.indexOf(opt);
      if (i > -1) self.setActive(i, false);
    }, true);

    /* A completion fills the field and searches; everything else is a link and
       is left alone. */
    this.el.addEventListener("click", function (e) {
      var q = e.target.closest("[data-cnc-ps-term]");
      if (q) {
        recentAdd(q.getAttribute("data-cnc-ps-term"));
        return;
      }
      var nav = e.target.closest("[data-cnc-ps-nav], .cnc-ps-foot");
      if (nav) recentAdd(input.value);
    });

    this.el.addEventListener("click", function (e) {
      if (e.target.closest("[data-cnc-ps-clearrecent]")) {
        e.preventDefault();
        recentClear();
        self.renderIdle();
      }
      var chip = e.target.closest("[data-cnc-ps-fill]");
      if (chip) {
        e.preventDefault();
        input.value = chip.getAttribute("data-cnc-ps-fill");
        recentAdd(input.value);
        var form = input.closest("form");
        if (form) form.submit(); else window.location.href = searchUrl(input.value);
      }
    });

    var form = input.closest("form");
    if (form) {
      form.addEventListener("submit", function () { recentAdd(input.value); });
    }
  };

  Panel.prototype.position = function () {
    /* Only the phone sheet needs a measured offset; the dropdown is anchored
       by the host's position:relative and needs nothing. */
    if (window.matchMedia("(min-width: 700px)").matches) return;
    var r = this.input.getBoundingClientRect();
    this.el.style.setProperty("--cnc-ps-top", Math.round(r.bottom + 8) + "px");
  };

  Panel.prototype.show = function () {
    if (this.open) { this.position(); return; }
    this.open = true;
    this.el.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
    this.position();
  };

  Panel.prototype.close = function () {
    if (!this.open) return;
    this.open = false;
    this.el.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
    this.active = -1;
  };

  Panel.prototype.collect = function () {
    this.options = Array.prototype.slice.call(this.el.querySelectorAll("[data-cnc-ps-opt]"));
    this.active = -1;
  };

  Panel.prototype.setActive = function (i, scroll) {
    if (this.active > -1 && this.options[this.active]) {
      this.options[this.active].classList.remove("is-active");
      this.options[this.active].setAttribute("aria-selected", "false");
    }
    this.active = i;
    var el = this.options[i];
    if (!el) { this.input.removeAttribute("aria-activedescendant"); return; }
    el.classList.add("is-active");
    el.setAttribute("aria-selected", "true");
    if (el.id) this.input.setAttribute("aria-activedescendant", el.id);
    if (scroll !== false && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  };

  Panel.prototype.onKey = function (e) {
    if (e.key === "Escape") {
      if (this.open) { e.preventDefault(); this.close(); }
      return;
    }
    if (e.key === "Tab") { this.close(); return; }
    if (!this.open || !this.options.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setActive((this.active + 1) % this.options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setActive(this.active <= 0 ? this.options.length - 1 : this.active - 1);
    } else if (e.key === "Enter") {
      /* Enter with nothing highlighted must submit the form — the customer
         typed a query and pressed Return, which is the oldest contract on the
         web and outranks anything the panel is showing. */
      if (this.active > -1 && this.options[this.active]) {
        e.preventDefault();
        this.options[this.active].click();
      }
    } else if (e.key === "Home") {
      e.preventDefault(); this.setActive(0);
    } else if (e.key === "End") {
      e.preventDefault(); this.setActive(this.options.length - 1);
    }
  };

  Panel.prototype.onType = function () {
    var self = this;
    var q = this.input.value.trim();

    clearTimeout(this.timer);
    if (this.abort) { this.abort.abort(); this.abort = null; }

    if (q.length < MIN_CHARS) { this.renderIdle(); return; }
    if (q === this.lastQuery && this.open) return;

    var hit = cacheGet(q);
    if (hit !== undefined) { this.paint(hit, q); return; }

    /* The loading state only appears if the request outlives the debounce plus
       a beat. On a fast connection the results arrive first and the customer
       never sees a spinner flash. */
    this.timer = setTimeout(function () { self.fetch(q); }, DEBOUNCE);
  };

  Panel.prototype.fetch = function (q) {
    var self = this;
    var ctrl = ("AbortController" in window) ? new AbortController() : null;
    this.abort = ctrl;

    var slow = setTimeout(function () {
      if (self.input.value.trim() === q) self.renderLoading();
    }, 220);

    fetch(suggestUrl(q, this.limit, SECTION), {
      signal: ctrl ? ctrl.signal : undefined,
      credentials: "same-origin"
    })
      .then(function (r) {
        if (!r.ok) throw new Error("suggest " + r.status);
        return r.text();
      })
      .then(function (html) {
        clearTimeout(slow);
        var doc = new DOMParser().parseFromString(html, "text/html");
        var body = doc.querySelector("[data-cnc-ps-body]");
        var out = body ? body.innerHTML : "";
        cacheSet(q, out);
        /* The customer may have typed on while this was in flight. */
        if (self.input.value.trim() === q) self.paint(out, q);
      })
      .catch(function (err) {
        clearTimeout(slow);
        if (err && err.name === "AbortError") return;
        /* Suggestions are an enhancement. A failed fetch closes the panel and
           leaves a working search form behind, which is the whole reason the
           field is a real GET form. */
        self.close();
      });
  };

  Panel.prototype.paint = function (html, q) {
    this.lastQuery = q;
    this.el.innerHTML =
      '<div class="cnc-ps-scroll">' + html + "</div>" +
      '<a class="cnc-ps-foot" href="' + esc(searchUrl(q)) + '" data-cnc-ps-opt data-cnc-ps-nav id="cnc-ps-o-all">' +
        "<span>See all results for &ldquo;" + esc(q) + "&rdquo;</span>" +
        '<span class="cnc-ps-foot__k">Return</span>' +
      "</a>";
    highlight(this.el, q);
    this.collect();
    this.show();
  };

  Panel.prototype.renderLoading = function () {
    this.el.innerHTML =
      '<div class="cnc-ps-load">' +
        '<span class="cnc-ps-load__dot"></span>' +
        '<span class="cnc-ps-load__dot"></span>' +
        '<span class="cnc-ps-load__dot"></span>' +
        "<span>Searching…</span>" +
      "</div>";
    this.options = [];
    this.active = -1;
    this.show();
  };

  /* Empty field: what we know about this shopper (recents) before what we know
     about everyone (trending). If we have neither, there is nothing worth
     opening a panel to say. */
  Panel.prototype.renderIdle = function () {
    var recents = recentRead();
    if (!recents.length && !this.trending.length) { this.close(); return; }

    var html = "";

    if (recents.length) {
      html +=
        '<div class="cnc-ps-group">' +
          '<div class="cnc-ps-recent__head">' +
            '<p class="cnc-ps-glabel">Recent</p>' +
            '<button type="button" class="cnc-ps-clear" data-cnc-ps-clearrecent>Clear</button>' +
          "</div>" +
          '<ul class="cnc-ps-list">' +
            recents.map(function (t, i) {
              return '<li><a class="cnc-ps-q" role="option" aria-selected="false"' +
                ' id="cnc-ps-o-r' + i + '" data-cnc-ps-opt data-cnc-ps-nav' +
                ' href="' + esc(searchUrl(t)) + '">' +
                '<span class="cnc-ps-q__ic" aria-hidden="true">' + CLOCK + "</span>" +
                '<span class="cnc-ps-q__t">' + esc(t) + "</span>" +
                "</a></li>";
            }).join("") +
          "</ul>" +
        "</div>";
    }

    if (this.trending.length) {
      html +=
        '<div class="cnc-ps-group">' +
          '<p class="cnc-ps-glabel">Popular right now</p>' +
          '<div class="cnc-ps-chips">' +
            this.trending.map(function (t, i) {
              return '<a class="cnc-ps-chip" role="option" aria-selected="false"' +
                ' id="cnc-ps-o-t' + i + '" data-cnc-ps-opt data-cnc-ps-nav' +
                ' href="' + esc(searchUrl(t)) + '">' + esc(t) + "</a>";
            }).join("") +
          "</div>" +
        "</div>";
    }

    this.el.innerHTML = '<div class="cnc-ps-scroll">' + html + "</div>";
    this.collect();
    this.show();
  };

  /* ── Mount ───────────────────────────────────────────────────────────────── */

  function mountAll() {
    var fields = document.querySelectorAll("[data-cnc-ps]");
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (f.dataset.cncPsBound) continue;
      f.dataset.cncPsBound = "1";
      try { new Panel(f); } catch (e) { /* one broken field must not stop the others */ }
    }
  }

  /* ── Results page: clear button ──────────────────────────────────────────── */

  function initClear() {
    var btn = document.querySelector("[data-cnc-find-clear]");
    var input = document.getElementById("cnc-find-input");
    if (!btn || !input) return;

    function sync() { btn.hidden = !input.value; }
    input.addEventListener("input", sync);
    btn.addEventListener("click", function () {
      input.value = "";
      sync();
      input.focus();
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
    sync();
  }

  /* ── Results page: did you mean ──────────────────────────────────────────
     Only ever runs on a zero-result page. Shopify has no spell-correction
     endpoint, so this asks the completion resource what it knows about
     progressively shorter versions of the query — the full string first, then
     the first word, then a four-character stem. "sqeaky ball" finds nothing;
     "sqea" finds nothing either, but "ball" does, and that is a better offer
     than an empty page.

     Anything identical to what they already typed is dropped, because
     suggesting the failed query back is worse than suggesting nothing. */
  function initDidYouMean() {
    var box = document.querySelector("[data-cnc-find-dym]");
    var list = document.querySelector("[data-cnc-find-dym-list]");
    var input = document.getElementById("cnc-find-input");
    if (!box || !list || !input) return;

    var q = (input.value || "").trim();
    if (q.length < 3) return;

    var stems = [];
    var first = q.split(/\s+/)[0];
    if (first && first !== q) stems.push(first);
    if (q.length > 4) stems.push(q.slice(0, 4));
    stems.unshift(q);

    var seen = Object.create(null);
    seen[q.toLowerCase()] = true;
    var found = [];

    function ask(i) {
      if (i >= stems.length || found.length >= 3) return done();
      fetch("/search/suggest.json?q=" + encodeURIComponent(stems[i]) +
            "&resources[type]=query&resources[limit]=5", { credentials: "same-origin" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var qs = (j && j.resources && j.resources.results && j.resources.results.queries) || [];
          qs.forEach(function (item) {
            var t = (item.text || "").trim();
            if (!t || seen[t.toLowerCase()] || found.length >= 3) return;
            seen[t.toLowerCase()] = true;
            found.push(t);
          });
          ask(i + 1);
        })
        .catch(function () { ask(i + 1); });
    }

    function done() {
      if (!found.length) return;
      list.innerHTML = found.map(function (t) {
        return '<a class="cnc-find-term" href="' + esc(searchUrl(t)) + '">' + esc(t) + "</a>";
      }).join("");
      box.hidden = false;
    }

    ask(0);
  }

  /* ── Headline vs grid ────────────────────────────────────────────────────
     The <h1> count is server-rendered from `search.results_count`, which is
     exact for every link this theme generates (they all carry type=product).
     A bookmark or an external link that drops the type makes Shopify count
     articles and pages too, and the grid — which only draws products — then
     shows fewer cards than the headline claims.

     Liquid cannot see that mismatch cheaply: reading the results outside the
     paginate tag returns only the first ten, which is the bug this replaced.
     Counting the rendered cards can, so the headline is corrected here if the
     two ever disagree. Silent and instant in the normal case, because they
     don't.

     Only the <h1> is touched. The toolbar count belongs to cnc-filters.js,
     which rewrites it on every filter change; writing it from here too would
     mean two owners and a stale number the moment someone filters. */
  function syncHeadline() {
    var numEl = document.querySelector("[data-cnc-find-n]");
    var nounEl = document.querySelector("[data-cnc-find-noun]");
    var grid = document.querySelector("[data-cnc-grid]");
    if (!numEl || !grid) return;

    var drawn = grid.querySelectorAll(".cnc-card").length;
    if (!drawn || String(drawn) === numEl.textContent.trim()) return;

    numEl.textContent = drawn;
    if (nounEl) nounEl.textContent = drawn === 1 ? "result" : "results";
  }

  function boot() {
    mountAll();
    initClear();
    initDidYouMean();
    syncHeadline();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The header is a section and can be re-rendered by the theme editor. */
  document.addEventListener("shopify:section:load", mountAll);

  window.CNC = window.CNC || {};
  window.CNC.mountPredictive = mountAll;
})();
